import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, MessageSquare, FileText, PenLine, CheckCircle } from "lucide-react";
import { supabase } from "../lib/supabase";
import { C } from "../theme";

const NOTIF_ICONS = {
  message: MessageSquare,
  document: FileText,
  signing: PenLine,
  signed: CheckCircle,
  default: Bell,
};

function NotifIcon({ type, color }) {
  const Icon = NOTIF_ICONS[type] || NOTIF_ICONS.default;
  return <Icon size={14} color={color || "#6B7280"} />;
}

function timeAgo(ts) {
  const diff = (Date.now() - new Date(ts)) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(ts).toLocaleDateString("en-ZA", { day: "numeric", month: "short" });
}

// ── Permission Prompt Banner — disabled ───────────────────────────
export function PushPermissionBanner() { return null; }

// ── Notification Bell Dropdown ─────────────────────────────────────
const DISMISSED_KEY = "notif_dismissed_ids";
function getDismissed() {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]")); } catch { return new Set(); }
}
function saveDismissed(set) {
  try { localStorage.setItem(DISMISSED_KEY, JSON.stringify([...set].slice(-200))); } catch {}
}

export default function NotificationBell({ primaryColor }) {
  const bc = primaryColor || C.orange;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const dropRef = useRef(null);
  const dismissedRef = useRef(getDismissed());

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const dismissed = dismissedRef.current;

      // Fetch recent unread client messages
      const { data: msgs } = await supabase
        .from("portal_messages")
        .select("id, client_id, content, attachment_name, created_at, read_by_practice, category")
        .eq("from_role", "client")
        .order("created_at", { ascending: false })
        .limit(20);

      // Fetch recent signing completions
      const { data: signed } = await supabase
        .from("signing_requests")
        .select("id, client_name, status, signed_at, created_at")
        .in("status", ["completed", "signed"])
        .order("signed_at", { ascending: false })
        .limit(5);

      const notifs = [];

      (msgs || []).forEach((m) => {
        let preview = "";
        if (m.category === "email_notification" || m.category === "email") {
          // Structured JSON — parse and show human-readable summary
          try {
            const d = JSON.parse(m.content);
            const files = d.files || [];
            if (m.category === "email_notification") {
              preview = `📧 ${d.subject || "(no subject)"}${files.length ? ` · ${files.length} file${files.length !== 1 ? "s" : ""}` : ""}`;
            } else {
              preview = `📧 ${d.subject || "(no subject)"}${d.body ? ` — ${d.body.slice(0, 50)}` : ""}`;
            }
          } catch { preview = "📧 Email received"; }
        } else {
          const raw = m.content || "";
          // Detect and parse legacy JSON content, format it human-readably
          if (raw.trimStart().startsWith("{")) {
            try {
              const d = JSON.parse(raw);
              if (d.status === "approved") preview = `✅ ${d.title || d.type_label || "Approved"}`;
              else if (d.status === "queried") preview = `❓ ${d.title || "Query raised"}${d.query_note ? `: ${d.query_note.slice(0, 50)}` : ""}`;
              else if (d.type_label || d.title) preview = `📋 ${d.type_label || ""} ${d.title ? `— ${d.title}` : ""}`.trim();
              else if (d.description) preview = d.description.slice(0, 80);
              else preview = "Approval activity";
            } catch { preview = raw.replace(/<[^>]+>/g, "").trim().slice(0, 80); }
          } else {
            preview = raw.replace(/<[^>]+>/g, "").trim().slice(0, 80);
          }
          preview = preview || m.attachment_name || "Sent an attachment";
        }
        notifs.push({
          id: `msg-${m.id}`,
          type: "message",
          read: m.read_by_practice || dismissed.has(`msg-${m.id}`),
          title: m.category === "email_notification" ? "Email filed to portal" : m.category === "email" ? "Email received" : "New client message",
          body: preview || "Sent a message",
          time: m.created_at,
          url: "/clients",
          color: "#3B82F6",
          bg: "#EFF6FF",
        });
      });

      (signed || []).forEach((s) => {
        const ts = s.signed_at || s.created_at;
        const ageMs = Date.now() - new Date(ts);
        if (ageMs < 7 * 24 * 3600 * 1000) { // only show if signed within 7 days
          notifs.push({
            id: `sign-${s.id}`,
            type: "signed",
            read: ageMs > 24 * 3600 * 1000 || dismissed.has(`sign-${s.id}`),
            title: "Document signed!",
            body: `${s.client_name || "A client"} just signed their engagement letter.`,
            time: ts,
            url: "/signed-documents",
            color: "#16A34A",
            bg: "#F0FDF4",
          });
        }
      });

      // Sort by time descending
      notifs.sort((a, b) => new Date(b.time) - new Date(a.time));
      setNotifications(notifs.slice(0, 15));
      setUnread(notifs.filter((n) => !n.read).length);
    } catch {}
    setLoading(false);
  }, []);

  // Fetch on mount and periodically
  useEffect(() => {
    fetchNotifications();
    const iv = setInterval(fetchNotifications, 30000);
    return () => clearInterval(iv);
  }, [fetchNotifications]);

  // Realtime: new client message → update count
  useEffect(() => {
    const ch = supabase
      .channel("notif-bell-practice")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "portal_messages", filter: "from_role=eq.client" }, () => {
        fetchNotifications();
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleOpen = () => {
    setOpen((v) => !v);
  };

  const handleNotifClick = async (notif) => {
    if (!notif.read) {
      // Persist dismissal locally so polls don't resurface it
      dismissedRef.current.add(notif.id);
      saveDismissed(dismissedRef.current);
      if (notif.type === "message") {
        const msgId = notif.id.replace("msg-", "");
        fetch("/.netlify/functions/portal-mark-read", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ msg_id: msgId }),
        }).catch(() => {});
      }
      setNotifications((prev) => prev.map((n) => n.id === notif.id ? { ...n, read: true } : n));
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    if (notif.url) navigate(notif.url);
  };

  const markAllRead = async () => {
    // Persist all current IDs as dismissed so polls don't resurface them
    notifications.forEach((n) => dismissedRef.current.add(n.id));
    saveDismissed(dismissedRef.current);
    fetch("/.netlify/functions/portal-mark-read", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mark_all: true }),
    }).catch(() => {});
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  return (
    <div ref={dropRef} style={{ position: "relative" }}>
      {/* Bell button */}
      <button
        onClick={handleOpen}
        title="Notifications"
        style={{
          position: "relative",
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 34, height: 34, borderRadius: 8,
          border: "1px solid #E5E7EB", background: open ? `${bc}12` : "#fff",
          color: open ? bc : "#6B7280", cursor: "pointer",
          transition: "all 0.15s",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = `${bc}12`; e.currentTarget.style.color = bc; e.currentTarget.style.borderColor = `${bc}40`; }}
        onMouseLeave={e => { if (!open) { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#6B7280"; e.currentTarget.style.borderColor = "#E5E7EB"; } }}
      >
        <Bell size={15} />
        {unread > 0 && (
          <span style={{
            position: "absolute", top: -4, right: -4,
            minWidth: 16, height: 16, borderRadius: 8,
            background: "#EF4444", color: "#fff",
            fontSize: 9, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 3px", border: "1.5px solid #fff",
            lineHeight: 1,
          }}>
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0,
          width: 340, maxHeight: 480,
          background: "#fff", borderRadius: 14,
          border: "1px solid #E5E7EB",
          boxShadow: "0 12px 48px rgba(0,0,0,0.14)",
          zIndex: 999, overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>
          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 16px 10px", borderBottom: "1px solid #F1F5F9",
          }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#004A50" }}>Notifications</div>
              {unread > 0 && (
                <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 1 }}>{unread} unread</div>
              )}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {unread > 0 && (
                <button onClick={markAllRead} style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 11, color: bc, fontWeight: 600, padding: "3px 6px",
                  borderRadius: 6, fontFamily: "inherit",
                }}>
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", padding: 2 }}>
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Notification list */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading && notifications.length === 0 && (
              <div style={{ padding: "24px 0", textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
                Loading…
              </div>
            )}
            {!loading && notifications.length === 0 && (
              <div style={{ padding: "32px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🎉</div>
                <div style={{ fontWeight: 700, color: "#004A50", fontSize: 13 }}>All caught up!</div>
                <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>No new notifications</div>
              </div>
            )}
            {notifications.map((notif) => (
              <button
                key={notif.id}
                onClick={() => handleNotifClick(notif)}
                style={{
                  width: "100%", textAlign: "left", border: "none",
                  background: notif.read ? "#fff" : `${notif.bg || "#EFF6FF"}`,
                  borderBottom: "1px solid #F8FAFC",
                  padding: "11px 16px",
                  cursor: "pointer", display: "flex", gap: 10, alignItems: "flex-start",
                  transition: "background 0.12s", fontFamily: "inherit",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#F8FAFC"; }}
                onMouseLeave={e => { e.currentTarget.style.background = notif.read ? "#fff" : (notif.bg || "#EFF6FF"); }}
              >
                <div style={{
                  width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                  background: notif.bg || "#EFF6FF",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <NotifIcon type={notif.type} color={notif.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: notif.read ? 500 : 700, fontSize: 12.5, color: "#004A50", lineHeight: 1.3 }}>
                    {notif.title}
                    {!notif.read && (
                      <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: notif.color || bc, marginLeft: 5, verticalAlign: "middle" }} />
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#64748B", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
                    {notif.body}
                  </div>
                  <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 3 }}>
                    {timeAgo(notif.time)}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div style={{ padding: "8px 16px", borderTop: "1px solid #F1F5F9", textAlign: "center" }}>
              <button onClick={() => { setOpen(false); navigate("/notifications"); }} style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 11, color: bc, fontWeight: 600, fontFamily: "inherit",
              }}>
                View all activity →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
